import { useCallback, useEffect, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { BellIcon, MailIcon } from "lucide-react";

import { OpenAPI } from "simplydo/interfaces";
import { Button } from "simplydo/react/ui/button";
import { Checkbox } from "simplydo/react/ui/checkbox";
import { Container } from "simplydo/react/ui/container";
import { Divider } from "simplydo/react/ui/divider";
import { H3, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import constants from "utils/constants";
import { supportsPush } from "utils/pushMessaging";
import util from "utils/utils";

import PushSetupModal from "components/lib/PushSetupModal";

import NotificationFrequency from "./NotificationFrequency";

type SubscriptionProps = {
  sub: (typeof constants.subscriptions)[number];
  hasSub: (
    key: keyof NonNullable<OpenAPI.Schemas["User"]["eventSubscriptions"]>,
    method: "email" | "push",
  ) => boolean;
  toggleSub: (
    key: keyof NonNullable<OpenAPI.Schemas["User"]["eventSubscriptions"]>,
    method: "email" | "push",
    checked: boolean,
  ) => void;
};

const Subscription = ({ sub, hasSub, toggleSub }: SubscriptionProps) => {
  const { t } = useTranslation();

  return (
    <TableRow>
      <TableCell>
        <p style={{ marginBottom: 2.5 }}>
          <em>{t(sub.title)}</em>
          <br />
        </p>
        {sub.text && (
          <span style={{ color: "gray", marginBottom: 5, fontSize: "0.9em" }}>
            {t(sub.text)}
          </span>
        )}
      </TableCell>
      <TableCell textAlign="center">
        <Checkbox
          data-testid={`test-email-${sub.key}`}
          style={{ margin: 5 }}
          checked={hasSub(sub.key, "email")}
          onChange={(_e, c) =>
            toggleSub(sub.key, "email", c.checked as boolean)
          }
        />
      </TableCell>
      <TableCell>
        <Checkbox
          data-testid={`test-push-${sub.key}`}
          style={{ margin: 5 }}
          checked={hasSub(sub.key, "push")}
          onChange={(_e, c) => toggleSub(sub.key, "push", c.checked as boolean)}
        />
      </TableCell>
    </TableRow>
  );
};

type NotificationSectionProps = {
  title: string;
  children: React.ReactNode;
  id: string;
};

function NotificationSection({
  title,
  children,
  id,
}: NotificationSectionProps) {
  return (
    <Table id={id}>
      <TableHeader>
        <TableRow>
          <TableHeaderCell>{title}</TableHeaderCell>
          <TableHeaderCell collapsing>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
              }}
            >
              <Icon icon={MailIcon} />
              <p>Email</p>
            </div>
          </TableHeaderCell>
          <TableHeaderCell collapsing>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
              }}
            >
              <Icon icon={BellIcon} />
              <p>Push</p>
            </div>
          </TableHeaderCell>
        </TableRow>
      </TableHeader>
      <TableBody>{children}</TableBody>
    </Table>
  );
}

const general = constants.subscriptions.filter((s) => s.type === "general");
const ideas = constants.subscriptions.filter((s) => s.type === "ideas");
const challenges = constants.subscriptions.filter(
  (s) => s.type === "challenges",
);
const groups = constants.subscriptions.filter((s) => s.type === "groups");

export function UserSettingsNotifications() {
  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();

  const onSubToggled = useCallback(
    (subs: OpenAPI.Schemas["User"]["eventSubscriptions"]) => {
      dispatch(actions.user.toggleEventSubscription(subs));
    },
    [dispatch],
  );

  const subs = user?.eventSubscriptions || {};

  const { t } = useTranslation();

  const [pushSetupOpen, setPushSetupOpen] = useState(() => {
    if (supportsPush() && Notification.permission === "default") {
      if (util.localStorageIsSupported()) {
        const hasSeenPushSetupModal = localStorage.getItem(
          "hasSeenPushSetupModal",
        );
        if (!hasSeenPushSetupModal) {
          localStorage.setItem("hasSeenPushSetupModal", "true");
          return true;
        }
      }
    }
    return false;
  });
  const [bulkAction, setBulkAction] = useState("all");

  useEffect(() => {
    if (!user?._id) return;
    api.users.getNotificationPreferences(
      user._id,
      (data) => {
        onSubToggled(data.eventSubscriptions);
      },
      () => {},
    );
  }, [user?._id, onSubToggled]);

  function hasSub(
    key: keyof NonNullable<OpenAPI.Schemas["User"]["eventSubscriptions"]>,
    method: "email" | "push",
  ) {
    if (key === "notificationFrequency") {
      return false;
    }
    const sub = subs[key];
    if (!sub) {
      return true;
    }
    return sub.indexOf(method) > -1;
  }

  function toggleSub(
    key: keyof NonNullable<OpenAPI.Schemas["User"]["eventSubscriptions"]>,
    method: "email" | "push",
    checked: boolean,
  ) {
    if (key === "notificationFrequency") {
      return;
    }

    let methods = [...(subs[key] || [])];
    if (!methods.length && checked) {
      methods = [method];
    }
    if (methods.length) {
      const index = methods.indexOf(method);
      if (checked && index === -1) {
        methods.push(method);
      } else if (!checked && index > -1) {
        methods.splice(index, 1);
      }
    }
    if (!user?._id) return;
    api.users.updateNotificationPreferences(
      user._id,
      [key],
      methods,
      (data) => {
        onSubToggled(data.eventSubscriptions);
      },
      (err) => toast.error(err.message),
    );
  }

  return (
    <Container>
      <PushSetupModal open={pushSetupOpen} setOpen={setPushSetupOpen} />
      <H3>
        {t("users.preferences.email.title")}
        <Subheader>{t("users.preferences.email.info")}</Subheader>
      </H3>

      <NotificationFrequency />

      <H3 style={{ display: "block" }}>
        Update preferences
        <Subheader>
          Use these controls to update all your preferences at once or use the
          checkboxes in the table below to control individual notification
          types.
        </Subheader>
      </H3>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <Checkbox
          type="radio"
          checked={bulkAction === "all"}
          value="all"
          onChange={(_e, { value }) => setBulkAction(value as string)}
        >
          Subscribe to all notifications
        </Checkbox>
        <Checkbox
          type="radio"
          checked={bulkAction === "emails"}
          value="emails"
          onChange={(_e, { value }) => setBulkAction(value as string)}
        >
          Subscribe to email notifications
        </Checkbox>
        <Checkbox
          type="radio"
          checked={bulkAction === "push"}
          value="push"
          onChange={(_e, { value }) => setBulkAction(value as string)}
        >
          Subscribe to push messaging
        </Checkbox>
        <Checkbox
          type="radio"
          checked={bulkAction === "unsubscribe"}
          value="unsubscribe"
          onChange={(_e, { value }) => setBulkAction(value as string)}
        >
          Unsubscribe from all
        </Checkbox>
        <div>
          <Button
            onClick={() => {
              const subscriptions = constants.subscriptions.map((s) => s.key);
              let methods: string[] = [];
              switch (bulkAction) {
                case "all":
                  methods = ["email", "push"];
                  break;
                case "emails":
                  methods = ["email"];
                  break;
                case "push":
                  methods = ["push"];
                  break;
                case "unsubscribe":
                  break;
                default:
                  break;
              }
              if (!user?._id) return;
              api.users.updateNotificationPreferences(
                user._id,
                subscriptions,
                methods,
                (data) => {
                  onSubToggled(data.eventSubscriptions);
                },
                (err) => toast.error(err.message),
              );
            }}
          >
            Apply
          </Button>
        </div>
      </div>

      <Divider />

      <H3 style={{ display: "block" }}>
        {t("users.preferences.email.action")}
      </H3>

      {supportsPush() && Notification.permission === "default" ? (
        <Message color="blue">
          <Icon icon={BellIcon} />
          <MessageHeader>Push notifications</MessageHeader>
          <MessageContent>
            You have not yet enabled push notifications.&nbsp;
            <a href="#" onClick={() => setPushSetupOpen(true)}>
              Click here
            </a>
            &nbsp;to enable them.
          </MessageContent>
        </Message>
      ) : null}

      {supportsPush() && Notification.permission === "denied" ? (
        <Message color="orange">
          You have turned off push notifications for {util.appName()} in this
          browser. You have to re-enable them in your browser settings to start
          receiving them again.
        </Message>
      ) : null}

      <NotificationSection
        title={t("users.preferences.yourAccount.title")}
        id="subscription-account"
      >
        {general
          .filter(
            (sub) =>
              sub.key !== "achievements" ||
              user?.ownerOrganisation?.achievementsEnabled !== false,
          )
          .map((sub) => (
            <Subscription
              key={sub.key}
              sub={sub}
              hasSub={hasSub}
              toggleSub={toggleSub}
            />
          ))}
      </NotificationSection>

      <Divider hidden />

      <NotificationSection
        title={t("common:capitalise", { key: "generic.groups" })}
        id="subscription-groups"
      >
        {groups.map((sub) => (
          <Subscription
            key={sub.key}
            sub={sub}
            hasSub={hasSub}
            toggleSub={toggleSub}
          />
        ))}
      </NotificationSection>

      <Divider hidden />

      <NotificationSection
        title={t("common:capitalise", { key: "generic.ideas" })}
        id="subscription-ideas"
      >
        {ideas.map((sub) => (
          <Subscription
            key={sub.key}
            sub={sub}
            hasSub={hasSub}
            toggleSub={toggleSub}
          />
        ))}
      </NotificationSection>

      <Divider hidden />

      <NotificationSection
        title={t("users.preferences.yourChallenges.title")}
        id="subscription-challenges"
      >
        {challenges.map((sub) => (
          <Subscription
            key={sub.key}
            sub={sub}
            hasSub={hasSub}
            toggleSub={toggleSub}
          />
        ))}
      </NotificationSection>

      <Divider hidden />
      <Message>{t("users.preferences.email.message")}</Message>
    </Container>
  );
}

export default UserSettingsNotifications;
